/**
 * ModuleErrorBoundary — a fronteira que impede UMA tela de apagar o PORTAL.
 *
 * ---------------------------------------------------------------------------
 * POR QUE ESTE ARQUIVO EXISTE, medido e não hipotético.
 *
 * Na revisão cruzada da fase de frota, o revisor interceptou o `fetch` e
 * devolveu o envelope que o servidor realmente emite. Uma linha com um campo a
 * menos produziu:
 *
 *     Uncaught TypeError: Cannot read properties of undefined (reading 'slice')
 *     document.body.innerText.length === 0
 *
 * **Zero.** Não caiu a tela de frota: caiu `AppShell`, `Protected`,
 * `BrowserRouter` e tudo que pendia deles. Carregadores, Subestação, Alertas —
 * o portal inteiro virou uma página em branco por causa de um campo de um
 * veículo. Não havia error boundary em lugar nenhum da árvore.
 *
 * O React desmonta a árvore INTEIRA quando um erro de render sobe sem ser
 * capturado. Essa é a regra, e a única defesa é uma classe com
 * `componentDidCatch` — não há forma em hook.
 *
 * ---------------------------------------------------------------------------
 * O TEXTO É O PONTO, e é por isso que a mensagem não é "algo deu errado".
 *
 * Esta é a mesma família de defeito que a fase inteira persegue: num portal de
 * operação, "algo deu errado" é lido como "o portal teve um soluço, mas a
 * operação está bem". O operador continua tocando o pátio com a informação que
 * tem na cabeça, que é justamente a que pode estar velha.
 *
 * A frase precisa dizer a coisa verdadeira e acionável: **este módulo não está
 * exibindo dado nenhum, e o que você vê em outras telas continua valendo.** É a
 * mesma disciplina de `nao-suportado` contra `sem-leitura`: nomear o estado em
 * vez de suavizá-lo.
 *
 * `role="alert"` e não `role="status"`: é falha, não atualização de estado.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { descricaoDoLancado } from '../lib/erro-lancado.js';

interface Props {
  /** Nome do módulo, para a frase dizer QUAL tela parou. */
  modulo: string;
  /** Muda quando a rota muda: força o boundary a tentar de novo na navegação. */
  chave: string;
  children: ReactNode;
}

interface State {
  /**
   * A SENTINELA É UM BOOLEANO, e não o valor lançado. Ver o cabeçalho de
   * `lib/erro-lancado.ts`: `null` e `undefined` são valores LANÇÁVEIS, e usar um
   * deles como "não houve erro" é construir a confusão entre AUSÊNCIA e
   * OCORRÊNCIA dentro do estado deste componente — a mesma classe de defeito que
   * a fase de frota persegue nos dados, aqui na peça que existe para conter
   * falha.
   */
  capturou: boolean;
  /** `unknown` porque é o que o React entrega. Ver o cabeçalho acima. */
  erro: unknown;
}

export class ModuleErrorBoundary extends Component<Props, State> {
  override state: State = { capturou: false, erro: undefined };

  /**
   * A ASSINATURA DIZ `unknown`, e a mudança de tipo é o conserto.
   *
   * Era `(erro: Error): State`, e o tipo MENTIA: `throw` aceita qualquer valor.
   * A mentira não custava um `tsc` vermelho — custava o portal em branco, porque
   * ela autorizava `erro.message` dez linhas abaixo. Ver `lib/erro-lancado.ts`
   * para o passo a passo medido no navegador.
   */
  static getDerivedStateFromError(erro: unknown): State {
    return { capturou: true, erro };
  }

  override componentDidUpdate(anterior: Props): void {
    /* Trocar de módulo LIMPA o erro. Sem isto, uma tela que quebrou deixaria o
       boundary preso e o operador veria a mesma falha ao navegar para outra
       coisa — o que reproduziria, em menor escala, exatamente o problema que
       este componente existe para resolver. */
    if (anterior.chave !== this.props.chave && this.state.capturou) {
      this.setState({ capturou: false, erro: undefined });
    }
  }

  /** `unknown` aqui também: `componentDidCatch` recebe o mesmo valor cru. */
  override componentDidCatch(erro: unknown, info: ErrorInfo): void {
    /* O `console.error` é deliberado: sem ele o erro deixa de existir para
       quem for diagnosticar, porque o boundary o consome. A pilha de
       componentes vai junto — é ela que diz QUAL linha da tabela quebrou.
       A DESCRIÇÃO entra na primeira linha porque `console.error(msg, undefined)`
       imprime "undefined" e mais nada: quem lê o log de um `throw undefined`
       precisa da frase, não do valor. */
    const { texto, foiError } = descricaoDoLancado(erro);
    console.error(
      `[nxvolt] O módulo "${this.props.modulo}" falhou ao renderizar e foi isolado: ${texto}` +
        (foiError ? '' : ' (o valor lançado não era um Error)'),
      erro,
      info.componentStack,
    );
  }

  override render(): ReactNode {
    if (!this.state.capturou) return this.props.children;
    /* NORMALIZA ANTES DE RENDERIZAR. `erro.message` sobre `unknown` é o estouro
       DENTRO do render do boundary — e erro lançado no render de um boundary não
       é capturado por ele: o React desmonta a árvore inteira. */
    const { texto, foiError } = descricaoDoLancado(this.state.erro);
    return (
      <section
        role="alert"
        className="nx-module-error"
        aria-labelledby="nx-module-error-title"
      >
        <h2 id="nx-module-error-title">
          {this.props.modulo}: esta tela não está exibindo dado nenhum
        </h2>
        <p>
          A tela parou antes de montar, então <strong>nada do que ela mostraria está
          na sua frente</strong> — nem em falta, nem em atraso: simplesmente não foi
          renderizado. Não tire conclusão sobre a frota, o pátio ou a subestação a partir
          desta tela agora.
        </p>
        <p>
          Os outros módulos do menu continuam funcionando e podem ser usados normalmente.
          Se a falha se repetir depois de recarregar, é defeito de software e vale abrir
          um chamado com o horário e o nome do módulo.
        </p>
        <p className="nx-module-error__tech">
          {/* `texto` NUNCA é vazio — invariante de `descricaoDoLancado`. Um
              `<code>` vazio é uma caixa cinza no lugar da causa, que era
              exatamente o que `throw 0`, `throw ''` e `throw {}` produziam: eles
              não estouravam, só não diziam nada. */}
          <code>
            {texto}
            {foiError ? '' : ' [o valor lançado não era um Error]'}
          </code>
        </p>
        <button
          type="button"
          className="nx-btn nx-btn--primary"
          onClick={() => this.setState({ capturou: false, erro: undefined })}
        >
          Tentar montar a tela de novo
        </button>
      </section>
    );
  }
}
